import { useEffect, useRef } from "react";
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useQueryClient,
} from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getFullName } from "@vega-api/custom/utils/user";
import { useUserProfile } from "@vega-api/extended/auth/hooks";
import {
  getGetConversationUnreadSummaryQueryKey,
  getListConversationsQueryKey,
  listConversations,
  useGetConversationUnreadSummary,
} from "@vega-api/generated/messaging-conversations-v1/messaging-conversations-v1";
import {
  getListMessagesQueryKey,
  listMessages,
  useSendMessage,
} from "@vega-api/generated/messaging-messages-v1/messaging-messages-v1";
import { useMarkConversationRead } from "@vega-api/generated/messaging-participants-v1/messaging-participants-v1";
import { type ApiResponseConversationPage } from "@vega-api/generated/types/apiResponseConversationPage";
import { type ApiResponseMessageWithMetaPage } from "@vega-api/generated/types/apiResponseMessageWithMetaPage";
import { MessageContentType } from "@vega-api/generated/types/messageContentType";
import { type MessageWithMetaResponse } from "@vega-api/generated/types/messageWithMetaResponse";
import { SenderType } from "@vega-api/generated/types/senderType";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

export const COINVEST_MESSAGING_PRODUCT_ID = "coinvest-deals-app";

export const getMessageSenderName = (message: MessageWithMetaResponse) =>
  getFullName({
    firstName: message.senderInfo?.firstName ?? undefined,
    lastName: message.senderInfo?.lastName ?? undefined,
  });

const CONVERSATIONS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 10;

const CONVERSATIONS_LIST_KEY = getListConversationsQueryKey()[0];
const UNREAD_SUMMARY_KEY = getGetConversationUnreadSummaryQueryKey()[0];
const MESSAGES_LIST_KEY = getListMessagesQueryKey()[0];

type ConversationsInfinite = InfiniteData<ApiResponseConversationPage>;

type MessagesInfinite = InfiniteData<ApiResponseMessageWithMetaPage>;

export const invalidateConversationQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [CONVERSATIONS_LIST_KEY] }),
    queryClient.invalidateQueries({ queryKey: [UNREAD_SUMMARY_KEY] }),
  ]);

export const invalidateMessagingStreamCaches = (queryClient: QueryClient) =>
  Promise.all([
    invalidateConversationQueries(queryClient),
    queryClient.invalidateQueries({ queryKey: [MESSAGES_LIST_KEY] }),
  ]);

type MessagingStreamEventPayload = {
  conversationId?: string;
};

export const handleMessagingStreamEvent = (
  queryClient: QueryClient,
  payload: unknown,
) => {
  const { conversationId } = payload as MessagingStreamEventPayload;

  void invalidateConversationQueries(queryClient);

  if (conversationId) {
    void queryClient.invalidateQueries({
      queryKey: getMessagesInfiniteQueryKey(conversationId),
    });
  }
};

export const useFetchNextPageErrorToast = (
  query: Pick<
    UseInfiniteQueryResult,
    "error" | "errorUpdateCount" | "isFetchNextPageError"
  >,
  loadErrorMessage: string,
) => {
  const showErrorToast = useCoInvestErrorToast();
  const toastedErrorCount = useRef(query.errorUpdateCount);

  useEffect(() => {
    if (
      query.isFetchNextPageError &&
      query.errorUpdateCount !== toastedErrorCount.current
    ) {
      showErrorToast(query.error as ErrorType<unknown>, {
        defaultMessage: loadErrorMessage,
      });
    }
    toastedErrorCount.current = query.errorUpdateCount;
  }, [
    query.error,
    query.errorUpdateCount,
    query.isFetchNextPageError,
    loadErrorMessage,
    showErrorToast,
  ]);
};

export const useInfiniteConversations = ({
  metaFilter,
  pageSize = CONVERSATIONS_PAGE_SIZE,
  enabled = true,
}: {
  metaFilter?: Record<string, string>;
  pageSize?: number;
  enabled?: boolean;
} = {}) => {
  const metadata =
    metaFilter && Object.keys(metaFilter).length > 0
      ? JSON.stringify(metaFilter)
      : undefined;

  const query = useInfiniteQuery({
    queryKey: [
      ...getListConversationsQueryKey(metadata ? { metadata } : undefined),
      "infinite",
      pageSize,
    ],
    queryFn: ({ pageParam }) =>
      listConversations({
        page: pageParam,
        pageSize,
        sort: ["-updatedAt"],
        metadata,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage === true
        ? (lastPage.meta.page ?? 0) + 1
        : undefined,
    enabled,
  });

  useFetchNextPageErrorToast(
    query,
    m.coInvestments_messages_loadConversationsError(),
  );

  return query;
};

const getMessagesInfiniteQueryKey = (conversationId: string) => [
  ...getListMessagesQueryKey({
    productId: COINVEST_MESSAGING_PRODUCT_ID,
    conversationId,
  }),
  "infinite",
];

export const useInfiniteConversationMessages = (conversationId: string) => {
  const query = useInfiniteQuery({
    queryKey: getMessagesInfiniteQueryKey(conversationId),
    queryFn: ({ pageParam }) =>
      listMessages({
        productId: COINVEST_MESSAGING_PRODUCT_ID,
        conversationId,
        page: pageParam,
        pageSize: MESSAGES_PAGE_SIZE,
        sort: ["createdAt:desc"],
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage === true
        ? (lastPage.meta.page ?? 0) + 1
        : undefined,
    enabled: conversationId !== "",
  });

  useFetchNextPageErrorToast(
    query,
    m.coInvestments_messages_loadMessagesError(),
  );

  return query;
};

export const useSendConversationMessage = (conversationId: string) => {
  const queryClient = useQueryClient();
  const showErrorToast = useCoInvestErrorToast();
  const profileQuery = useUserProfile();
  const messagesQueryKey = getMessagesInfiniteQueryKey(conversationId);

  return useSendMessage({
    mutation: {
      onMutate: async ({ data }) => {
        await queryClient.cancelQueries({ queryKey: messagesQueryKey });

        const previousMessages =
          queryClient.getQueryData<MessagesInfinite>(messagesQueryKey);

        const optimisticMessage: MessageWithMetaResponse = {
          messageId: crypto.randomUUID(),
          conversationId,
          senderId: profileQuery.data?.id ?? "",
          senderType: SenderType.USER,
          contentType: MessageContentType.TEXT,
          messageContent: data.messageContent,
          isRead: true,
          senderInfo: {
            firstName: profileQuery.data?.firstName ?? null,
            lastName: profileQuery.data?.lastName ?? null,
          },
          parentMessageId: null,
          createdAt: new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
        };

        queryClient.setQueryData<MessagesInfinite>(
          messagesQueryKey,
          (current) => {
            if (!current) return current;

            return {
              ...current,
              pages: current.pages.map((page, pageIndex) =>
                pageIndex === 0
                  ? { ...page, data: [optimisticMessage, ...page.data] }
                  : page,
              ),
            };
          },
        );

        return { previousMessages };
      },
      onError: (error, _variables, context) => {
        if (context?.previousMessages) {
          queryClient.setQueryData(messagesQueryKey, context.previousMessages);
        }
        showErrorToast(error, {
          defaultMessage: m.coInvestments_messages_sendError(),
        });
      },
      onSettled: () => invalidateConversationQueries(queryClient),
    },
  });
};

export const useUnreadMessagesTotal = ({
  dealId,
  enabled = true,
}: {
  dealId?: string;
  enabled?: boolean;
} = {}) => {
  const summaryQuery = useGetConversationUnreadSummary(
    {
      productId: COINVEST_MESSAGING_PRODUCT_ID,
      ...(dealId !== undefined && { metaFilter: [`dealId:${dealId}`] }),
    },
    { query: { enabled } },
  );

  return summaryQuery.data?.data.totalUnread ?? 0;
};

export const useAutoMarkConversationRead = ({
  conversationId,
  newestMessageId,
}: {
  conversationId: string;
  newestMessageId?: string;
}) => {
  const queryClient = useQueryClient();
  const showErrorToast = useCoInvestErrorToast();

  const markReadMutation = useMarkConversationRead({
    mutation: {
      onMutate: async ({ conversationId }) => {
        await queryClient.cancelQueries({
          queryKey: [CONVERSATIONS_LIST_KEY],
        });

        const previousLists = queryClient.getQueriesData<ConversationsInfinite>(
          {
            queryKey: [CONVERSATIONS_LIST_KEY],
          },
        );

        queryClient.setQueriesData<ConversationsInfinite>(
          { queryKey: [CONVERSATIONS_LIST_KEY] },
          (current) => {
            if (!current) return current;

            return {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: page.data.map((conversation) =>
                  conversation.id === conversationId &&
                  conversation.unreadCount > 0
                    ? { ...conversation, unreadCount: 0 }
                    : conversation,
                ),
              })),
            };
          },
        );

        return { previousLists };
      },
      onError: (error, _variables, context) => {
        context?.previousLists.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data),
        );
        showErrorToast(error, {
          defaultMessage: m.coInvestments_messages_markReadError(),
        });
      },
      onSettled: () => invalidateConversationQueries(queryClient),
    },
  });

  const markRead = markReadMutation.mutate;

  useEffect(() => {
    if (conversationId && newestMessageId) markRead({ conversationId });
  }, [conversationId, newestMessageId, markRead]);
};
